import React, { useState, useEffect } from "react"
import { Modal, Progress } from "antd"

/**
 * Props interface for the ProgressModal component
 */
interface ProgressModalProps {
  /** The title to display at the top of the modal */
  title: string
  /** A flag indicating whether the loading process is active */
  isLoading: boolean
  /** The duration in milliseconds over which the progress bar should fill */
  duration: number
  /**
   * For work whose length cannot be predicted (a server job that may take seconds or
   * minutes). Shows a looping bar instead of a percentage, rather than implying a
   * completion estimate the client does not have.
   */
  indeterminate?: boolean
  /** Optional line under the bar, e.g. the ordered stages the job runs through */
  subtitle?: string
}

/**
 * ProgressModal is a component that displays a modal with a progress bar.
 * The progress bar automatically updates based on a specified duration, or loops
 * when `indeterminate` is set.
 *
 * @component
 * @param {ProgressModalProps} props - The props for the component.
 * @returns {JSX.Element} The rendered ProgressModal component.
 */
const ProgressModal: React.FC<ProgressModalProps> = ({
  title,
  isLoading,
  duration,
  indeterminate = false,
  subtitle,
}) => {
  const [progress, setProgress] = useState<number>(0)

  useEffect(() => {
    if (isLoading && indeterminate) {
      // Sweeps 0 -> 90 and restarts: motion means "still working", never "nearly done".
      const interval: NodeJS.Timeout = setInterval(() => {
        setProgress((p) => (p >= 90 ? 0 : p + 2))
      }, 120)
      return (): void => {
        clearInterval(interval)
      }
    }
    if (isLoading) {
      const start: number = Date.now()

      const interval: NodeJS.Timeout = setInterval(() => {
        const elapsed: number = Date.now() - start
        const percent: number = Math.min((elapsed / duration) * 100, 100)
        setProgress(Math.round(percent))

        if (percent >= 100) {
          clearInterval(interval)
        }
      }, 100)

      return (): void => {
        clearInterval(interval)
      }
    } else {
      setProgress(0)
    }
  }, [isLoading, duration, indeterminate])

  return (
    <Modal title={title} open={isLoading} closable={false} footer={null}>
      <div className="flex w-full">
        <Progress
          percent={progress}
          status="active"
          strokeColor="var(--dq-brand)"
          showInfo={!indeterminate}
        />
      </div>
      {subtitle ? (
        <p className="text-xs text-gray-500 mt-2 mb-0">{subtitle}</p>
      ) : null}
    </Modal>
  )
}

export default ProgressModal
